import * as THREE from "three";
import { ROAD_WIDTH, LANE_X } from "./Lanes";

export const CHUNK_LENGTH = 30; // meters per chunk
export const CHUNK_COUNT = 6; // active chunks; ahead + behind player

/**
 * Recyclable road chunk: tarmac, lane lines, sidewalks, and street lamps.
 * The chunk's pivot is at z = its rear edge (highest z); the front edge
 * sits at z - CHUNK_LENGTH. Player is near z = 0 looking toward -z.
 */
export class RoadChunk {
  readonly group: THREE.Group;
  zRear = 0;

  constructor() {
    this.group = new THREE.Group();
    const tarmacMat = new THREE.MeshStandardMaterial({
      color: 0x1a1d28,
      roughness: 0.92,
      metalness: 0,
    });
    const sidewalkMat = new THREE.MeshStandardMaterial({
      color: 0x3a3f4c,
      roughness: 0.95,
      metalness: 0,
    });
    const lineMat = new THREE.MeshBasicMaterial({ color: 0xfff2c0 });

    const tarmac = new THREE.Mesh(
      new THREE.PlaneGeometry(ROAD_WIDTH, CHUNK_LENGTH),
      tarmacMat,
    );
    tarmac.rotation.x = -Math.PI / 2;
    tarmac.position.z = -CHUNK_LENGTH / 2;
    tarmac.position.y = 0;
    tarmac.receiveShadow = true;
    this.group.add(tarmac);

    // Sidewalks on either side
    const sw = ROAD_WIDTH / 2 + 2.5;
    const sidewalkGeo = new THREE.PlaneGeometry(5, CHUNK_LENGTH);
    [sw, -sw].forEach((x) => {
      const m = new THREE.Mesh(sidewalkGeo, sidewalkMat);
      m.rotation.x = -Math.PI / 2;
      m.position.set(x, 0.02, -CHUNK_LENGTH / 2);
      this.group.add(m);
    });

    // Lane lines: dashed white between lanes
    const dashLen = 1.2;
    const gap = 1.6;
    const dashGeo = new THREE.PlaneGeometry(0.12, dashLen);
    const dashCount = Math.floor(CHUNK_LENGTH / (dashLen + gap));
    for (let li = 0; li < LANE_X.length - 1; li++) {
      const x = (LANE_X[li] + LANE_X[li + 1]) / 2;
      for (let i = 0; i < dashCount; i++) {
        const dash = new THREE.Mesh(dashGeo, lineMat);
        dash.rotation.x = -Math.PI / 2;
        dash.position.set(x, 0.03, -i * (dashLen + gap) - dashLen / 2);
        this.group.add(dash);
      }
    }

    // Solid edge lines
    const edgeGeo = new THREE.PlaneGeometry(0.18, CHUNK_LENGTH);
    [ROAD_WIDTH / 2 - 0.1, -(ROAD_WIDTH / 2 - 0.1)].forEach((x) => {
      const e = new THREE.Mesh(edgeGeo, lineMat);
      e.rotation.x = -Math.PI / 2;
      e.position.set(x, 0.03, -CHUNK_LENGTH / 2);
      this.group.add(e);
    });

    // Street lamps every ~10m
    const lampPostMat = new THREE.MeshStandardMaterial({ color: 0x2a2e36 });
    const lampHeadMat = new THREE.MeshStandardMaterial({
      color: 0xffe2a8,
      emissive: 0xffc77a,
      emissiveIntensity: 1.5,
    });
    for (let i = 0; i < 3; i++) {
      const z = -i * 10 - 5;
      [-(ROAD_WIDTH / 2 + 1.5), ROAD_WIDTH / 2 + 1.5].forEach((x) => {
        const post = new THREE.Mesh(
          new THREE.CylinderGeometry(0.08, 0.08, 4, 8),
          lampPostMat,
        );
        post.position.set(x, 2, z);
        const head = new THREE.Mesh(
          new THREE.BoxGeometry(0.4, 0.2, 0.4),
          lampHeadMat,
        );
        head.position.set(x - Math.sign(x) * 0.7, 4, z);
        this.group.add(post, head);
      });
    }
  }

  setRearZ(z: number): void {
    this.zRear = z;
    this.group.position.z = z;
  }
}

/**
 * Pool of CHUNK_COUNT chunks recycled in a ring. Provides callbacks at
 * recycle time so the spawner can repopulate that chunk's contents.
 */
export class Road {
  readonly group: THREE.Group;
  readonly chunks: RoadChunk[] = [];
  private onRecycle: ((chunk: RoadChunk, index: number) => void) | null = null;

  constructor() {
    this.group = new THREE.Group();
    for (let i = 0; i < CHUNK_COUNT; i++) {
      const c = new RoadChunk();
      // Place chunks ahead of player. Player at z=0; chunks go from
      // +CHUNK_LENGTH (one behind) to -(CHUNK_COUNT-1)*CHUNK_LENGTH ahead.
      c.setRearZ(CHUNK_LENGTH - i * CHUNK_LENGTH);
      this.chunks.push(c);
      this.group.add(c.group);
    }
  }

  setRecycleHandler(fn: (chunk: RoadChunk, index: number) => void): void {
    this.onRecycle = fn;
  }

  /**
   * Scroll all chunks toward +z by `distance` meters. When a chunk's
   * front edge passes z = some-recycle-threshold, move it to the back.
   */
  scroll(distance: number): void {
    for (let i = 0; i < this.chunks.length; i++) {
      const c = this.chunks[i];
      c.setRearZ(c.zRear + distance);
      // Recycle if entire chunk is behind player by > CHUNK_LENGTH
      if (c.zRear - CHUNK_LENGTH > CHUNK_LENGTH) {
        // Find the most-forward chunk's front edge and place this one ahead of it.
        let minRear = Infinity;
        for (const o of this.chunks) if (o.zRear < minRear) minRear = o.zRear;
        c.setRearZ(minRear - CHUNK_LENGTH);
        this.onRecycle?.(c, i);
      }
    }
  }

  resetAll(): void {
    for (let i = 0; i < this.chunks.length; i++) {
      this.chunks[i].setRearZ(CHUNK_LENGTH - i * CHUNK_LENGTH);
    }
  }
}
